import { useCallback, useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle, Download } from 'lucide-react';
import { db } from '@/db/schema';
import { useDownloads } from '@/features/downloads/DownloadProvider';
import { formatBytes } from '@/lib/format';
import {
  checkSpaceFor,
  readStorageEstimate,
  type SpaceCheck,
} from '@/services/storage/StorageQuotaService';
import type { Sign } from '@/types/catalog';

/**
 * Download do glossário completo (F12-T19, F13-T08, PLAN §43).
 *
 * Mostra o resumo e pede confirmação **antes** de começar. Baixar gigabytes sem
 * avisar é a diferença entre uma viagem preparada e um plano de dados estourado.
 */
export function BatchDownloadCard(): React.ReactElement | null {
  const { canDownload, queueAll } = useDownloads();
  const [check, setCheck] = useState<SpaceCheck | null>(null);
  const [busy, setBusy] = useState(false);

  const pending = useLiveQuery(async () => {
    const signs = await db.signs.filter((sign) => sign.active).toArray();
    const downloadable = signs.filter((sign) => canDownload(sign));

    const states = await db.localSignState.bulkGet(downloadable.map((sign) => sign.id));
    const missing = downloadable.filter(
      (_sign, index) => states[index]?.offline.status !== 'available',
    );

    return { downloadable, missing };
    // `canDownload` é estável enquanto a configuração de mídia não muda.
  }, []);

  const estimateSpace = useCallback(async (missing: Sign[]) => {
    const estimate = await readStorageEstimate();

    // Só dá para somar o que a origem declara. Sem `expectedBytes`, o total fica
    // desconhecido e a checagem permite seguir — o erro de cota é tratado item a item.
    const declared = missing.reduce((sum, sign) => sum + (sign.media.expectedBytes ?? 0), 0);
    const known = missing.every((sign) => sign.media.expectedBytes !== undefined);

    setCheck(checkSpaceFor(known ? declared : null, estimate.availableBytes));
  }, []);

  useEffect(() => {
    if (!pending) return;
    void estimateSpace(pending.missing);
  }, [pending, estimateSpace]);

  // Sem origem de mídia configurada não há o que baixar, e o cartão não aparece.
  if (!pending || pending.downloadable.length === 0) return null;

  if (pending.missing.length === 0) {
    return (
      <section className="rounded-xl bg-emerald-500/10 p-4 text-sm text-emerald-300">
        Todos os {pending.downloadable.length} vídeos disponíveis já estão no dispositivo.
      </section>
    );
  }

  const requiredLabel =
    check?.requiredBytes != null ? formatBytes(check.requiredBytes) : 'desconhecido';

  return (
    <section className="rounded-xl bg-slate-800 p-4">
      <h2 className="font-medium">Baixar glossário completo</h2>

      <dl className="mt-3 space-y-1 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-slate-400">Vídeos</dt>
          <dd className="tabular-nums text-slate-200">{pending.missing.length}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-slate-400">Tamanho estimado</dt>
          <dd className="tabular-nums text-slate-200">{requiredLabel}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-slate-400">Espaço disponível</dt>
          <dd className="tabular-nums text-slate-200">
            {check?.availableBytes != null ? `~${formatBytes(check.availableBytes)}` : 'desconhecido'}
          </dd>
        </div>
      </dl>

      {check && !check.allowed && (
        <p
          role="alert"
          className="mt-3 flex items-start gap-2 rounded-lg bg-amber-500/15 p-3 text-sm text-amber-200"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
          {check.reason}
        </p>
      )}

      <button
        type="button"
        disabled={busy || (check !== null && !check.allowed)}
        onClick={() => {
          setBusy(true);
          void queueAll().finally(() => setBusy(false));
        }}
        className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-sky-500 text-sm font-medium text-slate-950 active:bg-sky-600 disabled:bg-slate-700 disabled:text-slate-500"
      >
        <Download size={18} aria-hidden />
        Baixar
      </button>
    </section>
  );
}
